import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Form, Formik, useFormikContext } from "formik";
import {
  Alert,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SelectWrapper from "../../../../../../../Components/FormComponents/Select";
import AddIcon from "@mui/icons-material/Add";
import TextfieldWrapper from "../../../../../../../Components/FormComponents/TextfieldWrapper";
import { useEffect } from "react";
import * as Yup from "yup";
import PropTypes from "prop-types";
import LinkCampaigns from "./LinkCampaigns";
import dayjs from "dayjs";
import DeletePlanningCampaign from "../DeletePlanningCampaign";
import SelectClient from "./SelectClient";

CampaignGroup.prototype = {
  campaignGroup: PropTypes.object,
  monthDefaults: PropTypes.object,

  createCampaign: PropTypes.func,
  updateCampaign: PropTypes.func,
  selectedCard: PropTypes.object,
  refetchFunction: PropTypes.func,
  deletePlanningCampaign: PropTypes.func, // Optional function to delete campaign planning
};

export default function CampaignGroup({
  campaignGroup,
  monthDefaults,
  deletePlanningCampaign = null, // Optional function to delete campaign planning
  createCampaign = null,
  updateCampaign = null,
  selectedCard,
  refetchFunction = null, // Optional function to refetch data after creation or update
}) {
  const [open, setOpen] = React.useState(false);
  const [submissionError, setSubmissionError] = React.useState(null);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      {campaignGroup ? (
        <IconButton onClick={handleClickOpen}>
          <EditIcon color="primary" />
        </IconButton>
      ) : (
        <Button
          startIcon={<AddIcon />}
          color="warning"
          onClick={handleClickOpen}
        >
          create campaign group
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {campaignGroup ? "Edit" : "Create"} Campaign Group
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: 0 }}>
          <DialogContentText>
            {campaignGroup
              ? "Update the campaign group by editing the form below."
              : "Create a new campaign group by filling out the form below."}{" "}
            {selectedCard?.date}
          </DialogContentText>
          {campaignGroup && (
            <Stack direction="row" justifyContent="flex-end">
              <DeletePlanningCampaign
                deletePlanningCampaign={deletePlanningCampaign}
                campaignGroup={campaignGroup}
              />
            </Stack>
          )}

          <Formik
            initialValues={{
              name: campaignGroup?.name || "",
              date: selectedCard?.date || "",
              planningType: selectedCard?.type || "",
              linkedCallCentreCampaignIds:
                campaignGroup?.linkedCallCentreCampaignIds || [],
              leadsType: campaignGroup?.leadsType || "",
              campaignLiveDate: campaignGroup?.campaignLiveDate
                ? dayjs(campaignGroup.campaignLiveDate).format("YYYY-MM-DD")
                : null,
              client: campaignGroup?.client || "",
              product: campaignGroup?.product || "",
              activeStatus: campaignGroup?.activeStatus ?? true,
              fullCapacity: campaignGroup?.fullCapacity || 0,
              calculationType: campaignGroup?.calculationType || "",
              pFactor: campaignGroup?.pFactor || 0,
              costPerSale: campaignGroup?.costPerSale || 0,
              collectionRatio: campaignGroup?.collectionRatio || 0,
              collectionRatioRate: campaignGroup?.collectionRatioRate || 0,
              leadFee: campaignGroup?.leadFee || 0,
              workingDays: monthDefaults?.workingDays || 0,
              workingHoursPerDay: monthDefaults?.workingHoursPerDay || 0,

              seatsPerClient: campaignGroup?.seatsPerClient || 0,
              currentSeats: campaignGroup?.currentSeats || 0,
              averagePremium: campaignGroup?.averagePremium || 0,
              biPerSale: campaignGroup?.biPerSale || 0,
              salesPerHour: campaignGroup?.salesPerHour || 0,
              salesToContactRatio: campaignGroup?.salesToContactRatio || 0,
              contactToLeads: campaignGroup?.contactToLeads || 0,
              confirmedLeads: campaignGroup?.confirmedLeads || 0,

              month: selectedCard?.date || null,
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmissionError(null); // Clear previous errors

              try {
                // --- IMPROVEMENT: Build a clean payload declaratively ---
                const payload = {
                  // Base fields that are always included
                  name: values.name,
                  campaignLiveDate: values.campaignLiveDate || null,
                  date: values.date || null,
                  month: values.month || null,
                  planningType: values.planningType,
                  linkedCallCentreCampaignIds:
                    values.linkedCallCentreCampaignIds,
                  leadsType: values.leadsType,
                  client: values.client,
                  product: values.product,
                  activeStatus: values.activeStatus,
                  fullCapacity: values.fullCapacity,
                  calculationType: values.calculationType,
                  workingDays: values.workingDays,
                  workingHoursPerDay: values.workingHoursPerDay,
                  seatsPerClient: values.seatsPerClient,
                  currentSeats: values.currentSeats,
                  averagePremium: values.averagePremium,
                  biPerSale: values.biPerSale,
                  salesPerHour: values.salesPerHour,
                  salesToContactRatio: values.salesToContactRatio,
                  contactToLeads: values.contactToLeads,
                  confirmedLeads: values.confirmedLeads,
                };

                // Conditionally add fields based on calculationType
                if (
                  [
                    "P Factor",
                    "P Factor / Collection Ratio",
                    "P Factor / Collection Ratio / Lead Fee",
                  ].includes(values.calculationType)
                ) {
                  payload.pFactor = values.pFactor;
                }
                if (
                  [
                    "P Factor / Collection Ratio",
                    "P Factor / Collection Ratio / Lead Fee",
                    "Assupol BI Calculation",
                    "Clientele Legal BI Calculation",
                  ].includes(values.calculationType)
                ) {
                  payload.collectionRatio = values.collectionRatio;
                }
                if (
                  ["Clientele Legal BI Calculation"].includes(
                    values.calculationType,
                  )
                ) {
                  payload.collectionRatioRate = values.collectionRatioRate;
                }
                if (
                  [
                    "Clientele Legal BI Calculation",
                    "Cost Per Sale (CPS)",
                  ].includes(values.calculationType)
                ) {
                  payload.costPerSale = values.costPerSale;
                }
                if (
                  [
                    "P Factor / Collection Ratio / Lead Fee",
                    "Assupol BI Calculation", // <-- ADD HERE
                  ].includes(values.calculationType)
                ) {
                  payload.leadFee = values.leadFee;
                }
                if (values.calculationType === "Assupol BI Calculation") {
                  payload.pFactorOnSale = values.pFactorOnSale;
                  payload.pFactorOnConversion = values.pFactorOnConversion;
                }

                if (campaignGroup) {
                  // --- Await the async update operation ---
                  await updateCampaign({ ...payload, id: campaignGroup.id });
                } else {
                  // --- Await the async create operation ---
                  await createCampaign(payload);
                }

                // If successful, refetch data and close the dialog
                if (refetchFunction) {
                  refetchFunction();
                }
                handleClose();
              } catch (error) {
                // --- IMPROVEMENT: Catch and display server errors ---
                console.error("Submission failed:", error);
                const errorMessage =
                  error.response?.data?.message ||
                  "An unexpected error occurred. Please try again.";
                setSubmissionError(errorMessage);
              } finally {
                // --- Ensure the form is always re-enabled ---
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, isSubmitting }) => {
              const showPFactor = [
                "P Factor",
                "P Factor / Collection Ratio",
                "P Factor / Collection Ratio / Lead Fee",
              ].includes(values.calculationType);
              const showCollectionRatio = [
                "P Factor / Collection Ratio",
                "P Factor / Collection Ratio / Lead Fee",
                "Assupol BI Calculation",
                "Clientele Legal BI Calculation",
              ].includes(values.calculationType);
              const showCostPerSale = [
                "Cost Per Sale (CPS)",
                "Clientele Legal BI Calculation",
              ].includes(values.calculationType);

              const showLeadFee = [
                "P Factor / Collection Ratio / Lead Fee",
                "Assupol BI Calculation", // <-- ADD HERE
              ].includes(values.calculationType);
              const showAssupolFields =
                values.calculationType === "Assupol BI Calculation";
              const showCollectionRatioRate =
                values.calculationType === "Clientele Legal BI Calculation";

              return (
                <Form>
                  <FormikValueObserver />
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <TextfieldWrapper
                        name="name"
                        label="Campaign Group Name"
                        fullWidth
                        required
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <SelectClient name="client" label="Client" fullWidth />
                    </Grid>
                    <Grid item xs={6}>
                      <SelectWrapper
                        name="leadsType"
                        label="Leads Type"
                        options={Lead_Type.map((type) => ({
                          value: type,
                          label: type,
                        }))}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <SelectWrapper
                        name="product"
                        label="Product"
                        fullWidth
                        required
                        options={products.map((product) => ({
                          value: product,
                          label: product,
                        }))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextfieldWrapper
                        name="campaignLiveDate"
                        label="Campaign Live Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    {/* <Grid item xs={6}>
                      <SelectWrapper
                        name="activeStatus"
                        label="Active Status"
                        fullWidth
                        required
                        options={[
                          { value: true, label: "Active" },
                          { value: false, label: "Inactive" },
                        ]}
                      />
                    </Grid> */}

                    <Grid item xs={12}>
                      <LinkCampaigns name="linkedCallCentreCampaignIds" />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Capacity & Performance Inputs
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="currentSeats"
                        label="Current Seats"
                        type="float"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="fullCapacity"
                        label="Full Capacity"
                        type="number"
                        fullWidth
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="seatsPerClient"
                        label="Seats Per Client"
                        type="float"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="averagePremium"
                        label="Average Premium"
                        type="float"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="biPerSale"
                        label="BI Per Sale"
                        type="float"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="salesPerHour"
                        label="Sales Per Hour"
                        type="float"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="salesToContactRatio"
                        label="Sales To Contact Ratio"
                        type="float"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="contactToLeads"
                        label="Contact To Leads"
                        type="float"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <TextfieldWrapper
                        name="confirmedLeads"
                        label="Confirmed Leads"
                        type="float"
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Calculation Type
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <SelectWrapper
                        name="calculationType"
                        label="Calculation Type"
                        fullWidth
                        required
                        options={calculationTypes.map((type) => ({
                          value: type.title,
                          label: type.title,
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack>
                        <Typography variant="body2" color="textSecondary">
                          {values.calculationType
                            ? calculationTypes.find(
                                (type) => type.title === values.calculationType,
                              )?.description || ""
                            : "Select a calculation type to see its description."}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {values.calculationType
                            ? calculationTypes.find(
                                (type) => type.title === values.calculationType,
                              )?.calculation || ""
                            : "Select a calculation type to see its formula."}
                        </Typography>
                      </Stack>
                    </Grid>

                    {showAssupolFields && (
                      <>
                        <Grid item xs={6}>
                          <TextfieldWrapper
                            name="pFactorOnSale"
                            label="P Factor on Sale"
                            type="float"
                            fullWidth
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextfieldWrapper
                            name="pFactorOnConversion"
                            label="P Factor on Conversion"
                            type="float"
                            fullWidth
                            required
                          />
                        </Grid>
                      </>
                    )}

                    {showPFactor && (
                      <Grid item xs={6}>
                        <TextfieldWrapper
                          name="pFactor"
                          label="P Factor"
                          type="float"
                          fullWidth
                          required
                        />
                      </Grid>
                    )}

                    {showCollectionRatio && (
                      <Grid item xs={6}>
                        <TextfieldWrapper
                          name="collectionRatio"
                          label="Collection Ratio"
                          type="number"
                          fullWidth
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    {showCollectionRatioRate && (
                      <Grid item xs={6}>
                        <TextfieldWrapper
                          name="collectionRatioRate"
                          label="Collection Ratio Rate"
                          type="float"
                          fullWidth
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    {showCostPerSale && (
                      <Grid item xs={6}>
                        <TextfieldWrapper
                          name="costPerSale"
                          label="Cost Per Sale"
                          type="number"
                          fullWidth
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    {showLeadFee && (
                      <Grid item xs={6}>
                        <TextfieldWrapper
                          name="leadFee"
                          label="Lead Fee"
                          type="number"
                          fullWidth
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                  {errors && Object.keys(errors).length > 0 && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <>
                        {Object.values(errors).map((error, index) => (
                          <Typography color="error" key={index}>
                            {error}
                          </Typography>
                        ))}
                      </>
                    </Grid>
                  )}
                  {submissionError && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Alert severity="error">{submissionError}</Alert>
                    </Grid>
                  )}
                  <DialogActions sx={{ my: 2 }}>
                    <Button onClick={handleClose} disabled={isSubmitting}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                    >
                      {/* --- IMPROVEMENT: Show loading indicator --- */}
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

// --- Helper Component to handle side-effects ---
const FormikValueObserver = () => {
  const { values, setFieldValue, initialValues } = useFormikContext();

  useEffect(() => {
    const type = values.calculationType;
    const usesPFactor = [
      "P Factor",
      "P Factor / Collection Ratio",
      "P Factor / Collection Ratio / Lead Fee",
    ].includes(type);
    const usesCollectionRatio = [
      "P Factor / Collection Ratio",
      "P Factor / Collection Ratio / Lead Fee",
      "Assupol BI Calculation", // <-- ADD HERE
      "Clientele Legal BI Calculation",
    ].includes(type);
    const usesCollectionRatioRate = [
      "Clientele Legal BI Calculation", // <-- ADD HERE
    ].includes(type);
    const usesCostPerSale = [
      "Cost Per Sale (CPS)",
      "Clientele Legal BI Calculation", // <-- ADD HERE
    ].includes(type);
    const usesLeadFee = [
      "P Factor / Collection Ratio / Lead Fee",
      "Assupol BI Calculation", // <-- ADD HERE
    ].includes(type);
    // --- ADD NEW LOGIC ---
    const usesAssupolFields = type === "Assupol BI Calculation";
    // --- END NEW LOGIC ---

    if (!usesPFactor) setFieldValue("pFactor", 0);
    if (!usesCollectionRatio) setFieldValue("collectionRatio", 0);
    if (!usesCollectionRatioRate) setFieldValue("collectionRatioRate", 0);
    if (!usesCostPerSale) setFieldValue("costPerSale", 0);
    if (!usesLeadFee) setFieldValue("leadFee", 0);
    // --- ADD NEW RESET LOGIC ---
    if (!usesAssupolFields) {
      setFieldValue("pFactorOnSale", 0);
      setFieldValue("pFactorOnConversion", 0);
    }
    // --- END NEW RESET LOGIC ---
  }, [values.calculationType, setFieldValue, initialValues]);

  return null;
};

// --- VALIDATION SCHEMA ---
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Campaign Group Name is required"),
  leadsType: Yup.string().required("Leads Type is required"),
  product: Yup.string().required("Product is required"),
  activeStatus: Yup.boolean().required("Active Status is required"),
  fullCapacity: Yup.number()
    .required("Full Capacity is required")
    .min(0, "Capacity cannot be negative")
    .max(100, "Capacity cannot exceed 100%"),
  calculationType: Yup.string().required("A Calculation Type must be selected"),

  // --- Conditional Validation ---
  pFactor: Yup.number().when("calculationType", {
    is: (type) =>
      [
        "P Factor",
        "P Factor / Collection Ratio",
        "P Factor / Collection Ratio / Lead Fee",
      ].includes(type),
    then: (schema) =>
      schema
        .required("P Factor is required for this type")
        .min(0.1, "P Factor must be greater than 0"),
    otherwise: (schema) => schema.notRequired(),
  }),

  collectionRatio: Yup.number().when("calculationType", {
    // --- UPDATE VALIDATION CONDITION ---
    is: (type) =>
      [
        "P Factor / Collection Ratio",
        "P Factor / Collection Ratio / Lead Fee",
        "Assupol BI Calculation", // <-- ADD HERE
        "Clientele Legal BI Calculation",
      ].includes(type),
    then: (schema) =>
      schema
        .required("Collection Ratio is required")
        .min(1, "Ratio must be between 1 and 100")
        .max(100, "Ratio cannot exceed 100%"),
    otherwise: (schema) => schema.notRequired(),
  }),
  costPerSale: Yup.number().when("calculationType", {
    is: (type) =>
      [
        "Cost Per Sale (CPS)", // <-- ADD HERE
        "Clientele Legal BI Calculation",
      ].includes(type),
    then: (schema) =>
      schema
        .required("Cost Per Sale is required")
        .min(1, "Cost must be at least R1"),
    otherwise: (schema) => schema.notRequired(),
  }),
  leadFee: Yup.number().when("calculationType", {
    // --- UPDATE VALIDATION CONDITION ---
    is: (type) =>
      [
        "P Factor / Collection Ratio / Lead Fee",
        "Assupol BI Calculation", // <-- ADD HERE
      ].includes(type),
    then: (schema) =>
      schema.required("Lead Fee is required").min(1, "Fee must be at least R1"),
    otherwise: (schema) => schema.notRequired(),
  }),

  pFactorOnSale: Yup.number().when("calculationType", {
    is: "Assupol BI Calculation",
    then: (schema) =>
      schema
        .required("P Factor on Sale is required")
        .min(0.01, "P Factor on Sale must be greater than 0"),
    otherwise: (schema) => schema.notRequired(),
  }),
  pFactorOnConversion: Yup.number().when("calculationType", {
    is: "Assupol BI Calculation",
    then: (schema) =>
      schema
        .required("P Factor on Conversion is required")
        .min(0.01, "P Factor on Conversion must be greater than 0"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// --- CALCULATION TYPES ARRAY ---
let calculationTypes = [
  {
    title: "P Factor",
    calculation: "average premium * p factor",
    description: "Calculates based on P Factor.",
  },
  {
    title: "Cost Per Sale (CPS)",
    calculation: "cost per sale",
    description: "Calculates based on a fixed Cost Per Sale.",
  },
  {
    title: "P Factor / Collection Ratio",
    calculation: "average premium * p factor * collection ratio",
    description: "Calculates based on P Factor and Collection Ratio.",
  },
  {
    title: "P Factor / Collection Ratio / Lead Fee",
    calculation:
      "(average premium * p factor * collection ratio) - ((leads required per seat / sales) * R70)",
    description:
      "Calculates based on P Factor, Collection Ratio, and Lead Fee.",
  },
  {
    title: "Assupol BI Calculation",
    calculation:
      "(((P factor on sale * average premium) + (P factor on conversion * average premium * collection ratio)) - ((1 / sales to leads ratio) * lead fee)) * sales",
    description:
      "A complex BI calculation involving multiple P factors, ratios, and fees.",
  },
  {
    title: "Assupol BI Calculation 2",
    calculation:
      "(((4 * Average premium * 0.50) + (3 * Average premium)) - (R500)) * sales",
    description: "A simplified BI calculation.",
  },
  {
    title: "Clientele Legal BI Calculation",
    calculation:
      "(sales X cost per sale) + (sales X rate per converted sale X collection ratio)",
    description:
      "Calculates BI based on sales and collection ratio, specific to Clientele.",
  },
];
// --- END OF CALCULATION TYPES ARRAY ---

const Lead_Type = [
  "Affinity",
  "Bulk",
  "Cancelled/Lapsed",
  "Cold calling",
  "HL",
  "Resell",
  "Retention",
  "Upsell",
  "WarmUp",
  "Xsell",
];

const products = [
  "Car Insurance",
  "Funeral",
  "H/C/Legal",
  "Life",
  "MV&H",
  "Personal Lines",
  "Tombstone",
  "Legal",
];
